/**
 * SignatureBlock
 *
 * Displays signing/completion information at the bottom of a note as a
 * chronological timeline of signature events.
 *
 * Every entry in signature_metadata is shown in the order it happened —
 * including author re-signs — so the block never hides history (e.g. a
 * re-sign landing after a supervisor co-sign) and the header can never
 * contradict the signatures listed below it.
 *
 * - Uses signature_metadata from the note when available (denormalized credentials)
 * - Falls back to signerName + completionTimestamp when metadata is absent
 * - "Electronically Signed" for signed notes
 * - "Signed — Awaiting Co-signature" when the first co-sign is pending
 * - "Re-signed — Awaiting Co-signature" when the note was re-signed/resubmitted
 *   and is back in the co-sign queue
 * - "Completed" for notes completed without signing
 */

import React from 'react';
import { DownloadIcon } from '@jotpsych/ui/icons/DownloadIcon';
import { CheckCircle as CheckCircleIcon, PendingActions as PendingIcon } from '@mui/icons-material';
import { alpha, Box, Divider, IconButton, Tooltip, Typography, useTheme } from '@mui/material';
import type { SignatureEntryResponse } from '@store/generated/notes';
import { parseUTCDate } from '@utils/dateUtils';
import { formatDate } from '@utils/utils';

import { NoteSectionContainer } from './layouts/NoteSectionContainer';

export interface SignatureBlockProps {
  /** Whether the note was electronically signed (vs just completed) */
  isSigned: boolean;
  /** Fallback display name when signature_metadata is absent */
  signerName: string;
  /** Fallback completion timestamp (Unix seconds) when signature_metadata is absent */
  completionTimestamp?: number;
  /** Denormalized signing entries from the note — used when present */
  signatureMetadata?: SignatureEntryResponse[];
  /** Handler for downloading signed PDF (only shown when provided) */
  onDownloadPdf?: () => void;
  /** Whether the note is awaiting a supervisor co-signature */
  awaitingCosign?: boolean;
}

/** A signature entry annotated with its chronological event label. */
interface SignatureEvent {
  entry: SignatureEntryResponse;
  /** "Signed" (first author), "Re-signed" (later author), or "Co-signed" (supervisor) */
  label: string;
}

/**
 * Order every signature entry chronologically and label each one by what
 * happened: the first author entry is the original signature, later author
 * entries are re-signs, and supervisor entries are co-signs.
 */
const buildSignatureEvents = (
  signatureMetadata: SignatureEntryResponse[],
): SignatureEvent[] => {
  const sorted = [...signatureMetadata].sort(
    (a, b) => parseUTCDate(a.signed_at).getTime() - parseUTCDate(b.signed_at).getTime(),
  );

  let authorEntriesSeen = 0;
  return sorted.map(entry => {
    if (entry.role === 'supervisor') {
      return { entry, label: 'Co-signed' };
    }
    authorEntriesSeen += 1;
    return { entry, label: authorEntriesSeen === 1 ? 'Signed' : 'Re-signed' };
  });
};

export const SignatureBlock: React.FC<SignatureBlockProps> = ({
  isSigned,
  signerName,
  completionTimestamp,
  signatureMetadata,
  onDownloadPdf,
  awaitingCosign = false,
}) => {
  const theme = useTheme();

  const accentColor = awaitingCosign
    ? theme.palette.warning.main
    : isSigned
      ? theme.palette.success.main
      : theme.palette.grey[500];

  const hasMetadata = signatureMetadata && signatureMetadata.length > 0;
  const events = hasMetadata ? buildSignatureEvents(signatureMetadata) : [];

  // A pending co-sign after any earlier signature activity beyond the original
  // signature (a co-sign already in the history, or an author re-sign) means
  // the note was re-signed/resubmitted back into the co-sign queue — say so.
  const wasResubmitted =
    awaitingCosign && events.some(e => e.label === 'Co-signed' || e.label === 'Re-signed');

  const headerText = awaitingCosign
    ? wasResubmitted
      ? 'Re-signed — Awaiting Co-signature'
      : 'Signed — Awaiting Co-signature'
    : isSigned
      ? 'Electronically Signed'
      : 'Completed';

  return (
    <NoteSectionContainer sx={{ mt: 2, mb: 4 }}>
      <Box
        sx={{
          border: 1,
          borderColor: alpha(accentColor, 0.3),
          borderRadius: 2,
          bgcolor: alpha(accentColor, 0.04),
          px: 3,
          py: 2.5,
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          {awaitingCosign
            ? <PendingIcon sx={{ fontSize: 18, color: accentColor }} />
            : <CheckCircleIcon sx={{ fontSize: 18, color: accentColor }} />
          }
          <Typography
            variant="overline"
            sx={{
              fontWeight: 600,
              letterSpacing: 1,
              color: accentColor,
              lineHeight: 1,
              flex: 1,
            }}
          >
            {headerText}
          </Typography>
          {onDownloadPdf && (
            <Tooltip title="Download Signed Note" placement="left">
              <IconButton
                onClick={onDownloadPdf}
                size="small"
                sx={{
                  color: 'text.secondary',
                  '&:hover': { color: accentColor },
                }}
                aria-label="Download signed note"
              >
                <DownloadIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Chronological signature timeline */}
        {hasMetadata ? (
          events.map((event, index) => (
            <React.Fragment key={index}>
              {index > 0 && <Divider sx={{ my: 1.5, borderColor: alpha(accentColor, 0.15) }} />}
              <SignerRow entry={event.entry} eventLabel={event.label} accentColor={accentColor} />
            </React.Fragment>
          ))
        ) : (
          <SignerRowFallback name={signerName} timestamp={completionTimestamp} />
        )}

        {/* Pending co-signature row */}
        {awaitingCosign && (
          <>
            <Divider sx={{ my: 1.5, borderColor: alpha(accentColor, 0.15) }} />
            <Typography
              variant="body2"
              sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.8rem' }}
            >
              A supervisor co-signature has been requested and is pending.
            </Typography>
          </>
        )}
      </Box>
    </NoteSectionContainer>
  );
};

// ============================================================================
// Sub-components
// ============================================================================

interface SignerRowProps {
  entry: SignatureEntryResponse;
  /** Event label for this entry: "Signed", "Re-signed", or "Co-signed" */
  eventLabel: string;
  accentColor: string;
}

const SignerRow: React.FC<SignerRowProps> = ({ entry, eventLabel }) => {
  const licenseDisplay =
    entry.license_number && entry.license_state
      ? `${entry.license_number} (${entry.license_state})`
      : entry.license_number || undefined;

  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            display: 'block',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontSize: '0.65rem',
            fontWeight: 600,
          }}
        >
          {eventLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
          {entry.name}
        </Typography>
        {entry.npi_number && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            NPI: {entry.npi_number}
          </Typography>
        )}
        {licenseDisplay && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            License: {licenseDisplay}
          </Typography>
        )}
        {entry.license_type && (
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
            {entry.license_type}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'right' }}
      >
        {formatDate(parseUTCDate(entry.signed_at).getTime() / 1000, 'both')}
      </Typography>
    </Box>
  );
};

interface SignerRowFallbackProps {
  name: string;
  timestamp?: number;
}

const SignerRowFallback: React.FC<SignerRowFallbackProps> = ({ name, timestamp }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.875rem' }}>
      {name}
    </Typography>
    {timestamp && (
      <Typography
        variant="caption"
        sx={{ color: 'text.secondary', whiteSpace: 'nowrap', flexShrink: 0, textAlign: 'right' }}
      >
        {formatDate(timestamp, 'both')}
      </Typography>
    )}
  </Box>
);
